import { Html } from '@react-three/drei';
import { CADMarker } from '../../../types/cad';

export const MARKER_COLORS: Record<CADMarker['type'], string> = {
  info: '#2196f3',
  warning: '#ff9800',
  error: '#f44336',
};

interface MarkerOverlayProps {
  markers: CADMarker[];
  modelRadius: number;
  onMarkerClick?: (marker: CADMarker, index: number) => void;
}

/**
 * Renders 3D sphere markers with HTML labels inside the Canvas.
 * Must be placed inside <Center> to share the model's coordinate space.
 */
export function MarkerOverlay({
  markers,
  modelRadius,
  onMarkerClick,
}: MarkerOverlayProps) {
  const radius = Math.max(modelRadius * 0.04, 0.8);

  return (
    <>
      {markers.map((m, i) => (
        <group key={`marker-${m.position.join(',')}-${i}`} position={m.position}>
          <mesh
            onPointerDown={(e) => {
              if (!onMarkerClick) return;
              e.stopPropagation();
              onMarkerClick(m, i);
            }}
            onPointerOver={(e) => {
              if (!onMarkerClick) return;
              e.stopPropagation();
              document.body.style.cursor = 'pointer';
            }}
            onPointerOut={(e) => {
              if (!onMarkerClick) return;
              e.stopPropagation();
              document.body.style.cursor = '';
            }}
          >
            <sphereGeometry args={[radius, 16, 16]} />
            <meshStandardMaterial
              color={MARKER_COLORS[m.type]}
              transparent
              opacity={0.7}
              emissive={MARKER_COLORS[m.type]}
              emissiveIntensity={0.8}
            />
          </mesh>
          <Html
            center
            distanceFactor={modelRadius * 0.5}
            className="pointer-events-none"
            zIndexRange={[1, 0]}
          >
            <div
              // Only the marker colour is data-driven; the rest are utilities.
              style={{ backgroundColor: MARKER_COLORS[m.type] }}
              className="-translate-y-5 whitespace-nowrap rounded-md px-2.5 py-1 text-[13px] font-semibold text-white shadow"
            >
              {m.label}
            </div>
          </Html>
        </group>
      ))}
    </>
  );
}
